import { useApiTestingLanguage } from './useApiTestingLanguage';
import React, { useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle
} from '../ui/dialog';
import { Calendar, RefreshCw } from 'lucide-react';

/**
 * Helper function to get method badge color
 */
const getMethodColor = (method) => {
  const colors = {
    GET: 'bg-blue-100 text-blue-800',
    POST: 'bg-green-100 text-green-800',
    PUT: 'bg-yellow-100 text-yellow-800',
    DELETE: 'bg-red-100 text-red-800',
    PATCH: 'bg-purple-100 text-purple-800'
  };
  return colors[method] || 'bg-gray-100 text-gray-800';
};

/**
 * Get current datetime in a specific timezone formatted for datetime-local input
 */
const getCurrentDateTimeInTimezone = (timezone) => {
  try {
    const now = new Date();
    // Format the current time in the selected timezone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(now);
    const values = {};
    parts.forEach(part => {
      values[part.type] = part.value;
    });
    
    // Format as YYYY-MM-DDTHH:MM for datetime-local input
    return `${values.year}-${values.month}-${values.day}T${values.hour}:${values.minute}`;
  } catch (e) {
    // Fallback to local time if timezone conversion fails
    const now = new Date();
    return now.toISOString().slice(0, 16);
  }
};

/**
 * Get current date in a specific timezone formatted for date input
 */
const getCurrentDateInTimezone = (timezone) => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    return formatter.format(now);
  } catch (e) {
    return new Date().toISOString().split('T')[0];
  }
};

/**
 * ScheduleModal - Schedule API Test Execution Modal
 * 
 * Allows users to schedule one-time or recurring test executions
 * with timezone selection and notification preferences.
 */
export const ScheduleModal = ({
  // Modal state
  open,
  onOpenChange,
  // Test being scheduled
  selectedTest,
  // Form state
  scheduleForm,
  setScheduleForm,
  // Actions
  onCreateSchedule,
  loading
}) => {
  const { t } = useApiTestingLanguage();
  // Calculate minimum datetime based on selected timezone
  const minDateTime = useMemo(() => {
    return getCurrentDateTimeInTimezone(scheduleForm.timezone);
  }, [scheduleForm.timezone]);

  // Calculate minimum date based on selected timezone
  const minDate = useMemo(() => {
    return getCurrentDateInTimezone(scheduleForm.timezone);
  }, [scheduleForm.timezone]);

  if (!selectedTest) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("Schedule Test Execution")}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Test Information */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center space-x-3">
              <Badge className={getMethodColor(selectedTest.method)}>
                {selectedTest.method}
              </Badge>
              <div>
                <p className="font-medium text-blue-900">{selectedTest.name}</p>
                <p className="text-sm text-blue-700">{selectedTest.url}</p>
              </div>
            </div>
          </div>

          {/* Schedule Type */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-3">
              {t("Schedule Type")}
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div
                className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  scheduleForm.schedule_type === 'one_time'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setScheduleForm({...scheduleForm, schedule_type: 'one_time'})}
                data-testid="schedule-type-one-time"
              >
                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  <div>
                    <p className="font-medium">{t("One-time")}</p>
                    <p className="text-sm text-gray-600">{t("Run once at a specific time")}</p>
                  </div>
                </div>
              </div>
              <div
                className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  scheduleForm.schedule_type === 'recurring'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setScheduleForm({...scheduleForm, schedule_type: 'recurring'})}
                data-testid="schedule-type-recurring"
              >
                <div className="flex items-center space-x-3">
                  <RefreshCw className="h-5 w-5 text-purple-500" />
                  <div>
                    <p className="font-medium">{t("Recurring")}</p>
                    <p className="text-sm text-gray-600">{t("Run on a regular schedule")}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Timezone Selection - MOVED UP so min dates work correctly */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">
              {t("Timezone")}
            </label>
            <select
              value={scheduleForm.timezone}
              onChange={(e) => setScheduleForm({...scheduleForm, timezone: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-md"
              data-testid="schedule-timezone"
            >
              <option value="America/New_York">{t("Eastern Time (EST/EDT)")}</option>
              <option value="America/Chicago">{t("Central Time (CST/CDT)")}</option>
              <option value="America/Denver">{t("Mountain Time (MST/MDT)")}</option>
              <option value="America/Los_Angeles">{t("Pacific Time (PST/PDT)")}</option>
              <option value="UTC">UTC</option>
              <option value="Europe/London">{t("London (GMT/BST)")}</option>
              <option value="Europe/Paris">{t("Paris (CET/CEST)")}</option>
              <option value="Asia/Tokyo">{t("Tokyo (JST)")}</option>
              <option value="Asia/Shanghai">{t("Shanghai (CST)")}</option>
              <option value="Australia/Sydney">{t("Sydney (AEST/AEDT)")}</option>
            </select>
          </div>

          {/* One-time Schedule Fields */}
          {scheduleForm.schedule_type === 'one_time' && (
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                {t("Scheduled Date & Time")} <span className="text-gray-500 font-normal">({scheduleForm.timezone})</span>
              </label>
              <Input
                type="datetime-local"
                value={scheduleForm.scheduled_datetime}
                onChange={(e) => setScheduleForm({...scheduleForm, scheduled_datetime: e.target.value})}
                min={minDateTime}
                data-testid="schedule-datetime"
              />
            </div>
          )}

          {/* Recurring Schedule Fields */}
          {scheduleForm.schedule_type === 'recurring' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  {t("Recurrence Pattern")}
                </label>
                <select
                  value={scheduleForm.recurrence_pattern}
                  onChange={(e) => setScheduleForm({...scheduleForm, recurrence_pattern: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-md"
                  data-testid="schedule-recurrence-pattern"
                >
                  <option value="daily">{t("Daily")}</option>
                  <option value="weekly">{t("Weekly")}</option>
                  <option value="monthly">{t("Monthly")}</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    {t("Start Date")} <span className="text-gray-500 font-normal">({scheduleForm.timezone})</span>
                  </label>
                  <Input
                    type="date"
                    value={scheduleForm.start_date}
                    onChange={(e) => setScheduleForm({...scheduleForm, start_date: e.target.value})}
                    min={minDate}
                    data-testid="schedule-start-date"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    {t("Time of Day")}
                  </label>
                  <Input
                    type="time"
                    value={scheduleForm.time_of_day}
                    onChange={(e) => setScheduleForm({...scheduleForm, time_of_day: e.target.value})}
                    data-testid="schedule-time"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  {t("End Date (Optional)")}
                </label>
                <Input
                  type="date"
                  value={scheduleForm.end_date}
                  onChange={(e) => setScheduleForm({...scheduleForm, end_date: e.target.value})}
                  placeholder={t("Leave blank to run indefinitely")}
                  data-testid="schedule-end-date"
                />
              </div>
            </div>
          )}

          {/* Notification Settings */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-3">
              {t("Notifications")}
            </label>
            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={scheduleForm.email_notifications}
                  onChange={(e) => setScheduleForm({...scheduleForm, email_notifications: e.target.checked})}
                  className="rounded"
                  data-testid="schedule-email-notifications"
                />
                <div>
                  <span className="text-sm font-medium">{t("Email Notifications")}</span>
                  <p className="text-xs text-gray-600">{t("Get email alerts when scheduled tests complete")}</p>
                </div>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={scheduleForm.in_app_notifications}
                  onChange={(e) => setScheduleForm({...scheduleForm, in_app_notifications: e.target.checked})}
                  className="rounded"
                  data-testid="schedule-in-app-notifications"
                />
                <div>
                  <span className="text-sm font-medium">{t("In-App Notifications")}</span>
                  <p className="text-xs text-gray-600">{t("Show notifications in the app when tests complete")}</p>
                </div>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
              data-testid="schedule-cancel-btn"
            >
              {t("Cancel")}
            </Button>
            <Button
              onClick={onCreateSchedule}
              disabled={loading}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              data-testid="schedule-create-btn"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {t("Creating...")}
                </>
              ) : (
                <>
                  <Calendar className="h-4 w-4 mr-2" />
                  {t("Create Schedule")}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleModal;
